import React, { useState } from 'react';
import { ProgressModalData } from '../../../types/assessment-progress';
import { getRelativeTime, getStepDisplayName } from '../../../utils/assessmentStorage';
import styles from './ProgressModal.module.css';

interface ProgressModalProps {
  data: ProgressModalData;
  onContinue: () => void;
  onStartNew: () => void;
  isOpen: boolean;
}

/**
 * Modal to prompt user to continue saved progress or start fresh
 */
export const ProgressModal: React.FC<ProgressModalProps> = ({
  data,
  onContinue,
  onStartNew,
  isOpen,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isOpen) return null;

  const handleStartNew = () => {
    if (!showConfirm) {
      setShowConfirm(true);
    } else {
      onStartNew();
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {!showConfirm ? (
          <>
            <div className={styles.header}>
              <div className={styles.icon}>
                <i className="fas fa-history"></i>
              </div>
              <h2>Welcome Back!</h2>
              <p className={styles.subtitle}>You have saved progress from a previous session</p>
            </div>

            <div className={styles.content}>
              <div className={styles.progressInfo}>
                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <i className="fas fa-industry"></i>
                    Industry
                  </div>
                  <div className={styles.infoValue}>{data.industry}</div>
                </div>

                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <i className="fas fa-user-tie"></i>
                    Role
                  </div>
                  <div className={styles.infoValue}>{data.role}</div>
                </div>

                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <i className="fas fa-tasks"></i>
                    Current Step
                  </div>
                  <div className={styles.infoValue}>
                    {getStepDisplayName(data.currentStep as any)}
                  </div>
                </div>

                {data.progress && data.progress.total > 0 && (
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabel}>
                      <i className="fas fa-chart-line"></i>
                      Progress
                    </div>
                    <div className={styles.infoValue}>
                      {data.progress.answered} of {data.progress.total} questions answered
                    </div>
                  </div>
                )}

                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <i className="fas fa-clock"></i>
                    Last Saved
                  </div>
                  <div className={styles.infoValue}>
                    {getRelativeTime(data.lastSaved)}
                  </div>
                </div>
              </div>

              {/* <div className={styles.progressBar}>
                <div className={styles.progressBarLabel}>Overall Progress</div>
                <div className={styles.progressBarTrack}>
                  <div
                    className={styles.progressBarFill}
                    style={{
                      width: data.progress
                        ? `${(data.progress.answered / data.progress.total) * 100}%`
                        : '33%', // Default based on step
                    }}
                  />
                </div>
              </div> */}
            </div>

            <div className={styles.actions}>
              <button onClick={onContinue} className={styles.primaryButton}>
                <i className="fas fa-play"></i>
                Continue Where I Left Off
              </button>
              <button onClick={handleStartNew} className={styles.secondaryButton}>
                <i className="fas fa-redo"></i>
                Start New Assessment
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.header}>
              <div className={`${styles.icon} ${styles.warningIcon}`}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h2>Start New Assessment?</h2>
              <p className={styles.subtitle}>
                This will permanently delete your saved progress
              </p>
            </div>

            <div className={styles.content}>
              <div className={styles.warningMessage}>
                <p>
                  <strong>Are you sure?</strong> Your current progress including:
                </p>
                <ul>
                  <li>Industry and role selections</li>
                  <li>All answered questions</li>
                  <li>Any generated personalized questions</li>
                </ul>
                <p>...will be permanently deleted and cannot be recovered.</p>
              </div>
            </div>

            <div className={styles.actions}>
              <button onClick={handleCancel} className={styles.primaryButton}>
                <i className="fas fa-arrow-left"></i>
                Go Back
              </button>
              <button onClick={onStartNew} className={styles.dangerButton}>
                <i className="fas fa-trash"></i>
                Delete Progress & Start New
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProgressModal;

